import { useCallback, useEffect, useState } from "react";
import { SlidersHorizontal, ShieldCheck, Save, Lock } from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { Loading } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

const LEVEL_LABEL = { automatic: "Automatic", approval: "Require approval", off: "Off" };
const LEVEL_STYLE = { automatic: "bg-emerald-600 text-white", approval: "bg-amber-500 text-white", off: "bg-slate-200 text-slate-600" };

const CELL_STYLE = (v) => {
  if (v === "Auto-execute") return "bg-emerald-50 text-emerald-700";
  if (v === "Your approval") return "bg-amber-50 text-amber-700";
  if (v === "CEO exception") return "bg-red-50 text-red-600";
  return "text-slate-500";
};

export default function OperatingMode() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => { api.get("/ops/operating-mode").then(({ data }) => setData(data)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  const { settings, modes, matrix, autopilot_modes, action_types, levels } = data;

  const patch = async (updates, quiet) => {
    setData((d) => ({ ...d, settings: { ...d.settings, ...updates } }));
    try {
      const { data: s } = await api.post("/ops/operating-mode", updates);
      setData((d) => ({ ...d, settings: s }));
      if (!quiet) toast.success("Operating mode updated");
    } catch { toast.error("Could not save. Please try again."); load(); }
  };
  const saveLimits = async () => {
    setSaving(true);
    await patch({ max_auto_sends_per_day: settings.max_auto_sends_per_day, owner_report_hour: settings.owner_report_hour, email_report: settings.email_report });
    setSaving(false);
  };
  const setActionPerm = async (action, level) => {
    const prev = settings.action_permissions;
    setData((d) => ({ ...d, settings: { ...d.settings, action_permissions: { ...d.settings.action_permissions, [action]: level } } }));
    try { const { data: s } = await api.post("/ops/action-permission", { action, level }); setData((d) => ({ ...d, settings: s })); toast.success("Permission updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update."); setData((d) => ({ ...d, settings: { ...d.settings, action_permissions: prev } })); }
  };

  return (
    <div className="animate-fade-up space-y-6" data-testid="operating-mode-page">
      <div>
        <h1 className="flex items-center gap-2 font-head text-2xl font-extrabold tracking-tight text-slate-900"><SlidersHorizontal size={22} className="text-[#b0862f]" /> Operating Mode</h1>
        <p className="mt-1 text-[13px] text-slate-500">Set the rules your Capital Crew runs on — how much it does automatically, and what still comes to you.</p>
      </div>

      {/* Mode cards */}
      <div className="grid gap-4 sm:grid-cols-2">
        {modes.map((m) => {
          const active = settings.mode === m.name;
          return (
            <button key={m.id} onClick={() => patch({ mode: m.name })} data-testid={`mode-${m.id}`}
              className={`rounded-2xl border p-5 text-left transition-colors ${active ? "border-[#080d1a] bg-[#080d1a] text-white" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <div className="flex items-center justify-between">
                <div className={`font-head text-[15px] font-bold ${active ? "text-white" : "text-slate-900"}`}>{m.name}</div>
                {active && <ShieldCheck size={16} className="text-[#b0862f]" />}
              </div>
              <p className={`mt-1.5 text-[13px] leading-relaxed ${active ? "text-slate-300" : "text-slate-500"}`}>{m.desc}</p>
              {autopilot_modes.includes(m.name) && <span className={`mt-2 inline-block font-mono text-[9px] font-bold uppercase tracking-wider ${active ? "text-[#b0862f]" : "text-emerald-600"}`}>Auto-Pilot default ON</span>}
            </button>
          );
        })}
      </div>

      {/* Policy matrix */}
      <Card className="overflow-x-auto p-5" data-testid="policy-matrix">
        <div className="mb-3 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">Autonomy policy matrix — what happens to each action</div>
        <table className="w-full min-w-[720px] text-left text-[12px]">
          <thead>
            <tr className="font-mono text-[9px] uppercase tracking-wider text-slate-400">
              <th className="pb-2 pr-3">Mode</th><th className="pb-2 pr-3">Low risk</th><th className="pb-2 pr-3">Medium risk</th>
              <th className="pb-2 pr-3">High risk</th><th className="pb-2 pr-3">QC failed</th><th className="pb-2 pr-3">Runs queued work</th><th className="pb-2">You see</th>
            </tr>
          </thead>
          <tbody>
            {matrix.map((row) => (
              <tr key={row.mode} className={`border-t border-slate-100 ${settings.mode === row.mode ? "bg-[#b0862f]/5" : ""}`} data-testid={`matrix-row-${row.mode}`}>
                <td className="py-2.5 pr-3"><span className={`rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider ${settings.mode === row.mode ? "bg-[#080d1a] text-white" : "bg-slate-100 text-slate-600"}`}>{row.mode}</span></td>
                {["low", "medium", "high", "qc_failed"].map((k) => (
                  <td key={k} className="py-2.5 pr-3"><span className={`rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${CELL_STYLE(row[k])}`}>{row[k]}</span></td>
                ))}
                <td className="py-2.5 pr-3 text-slate-500">{row.runs_queued}</td>
                <td className="py-2.5 text-slate-500">{row.you_see}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-3 text-[11px] leading-relaxed text-slate-400">High-risk actions (refunds, payment plans, legal, compliance, applications, identity) are never automated in any mode. Auto-sends stop at your daily cap. Every autonomous action is written to the audit trail with agent, reason, model, confidence and approval status.</p>
      </Card>

      {/* Permissions by action type */}
      <Card className="p-5" data-testid="action-permissions">
        <div className="mb-1 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">Permissions by action type</div>
        <p className="mb-4 text-[12px] text-slate-500">Control exactly what your Capital Crew can do on its own. High-risk actions always require your approval and can never be automated.</p>
        <div className="space-y-2">
          {action_types.map((a) => {
            const current = settings.action_permissions?.[a.id] || "approval";
            const options = a.high_risk ? levels.filter((l) => l !== "automatic") : levels;
            return (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-100 px-4 py-2.5" data-testid={`action-row-${a.id}`}>
                <div className="flex items-center gap-2">
                  <span className="font-head text-[13px] font-semibold text-slate-900">{a.label}</span>
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-slate-400">{a.group}</span>
                  {a.high_risk && <span className="inline-flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-red-600"><Lock size={9} /> High risk</span>}
                </div>
                <div className="flex gap-1">
                  {options.map((l) => (
                    <button key={l} onClick={() => setActionPerm(a.id, l)} data-testid={`action-${a.id}-${l}`}
                      className={`rounded-lg px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors ${current === l ? LEVEL_STYLE[l] : "bg-white text-slate-500 border border-slate-200 hover:border-slate-300"}`}>
                      {LEVEL_LABEL[l]}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Limits & owner report */}
      <Card className="p-5" data-testid="limits-card">
        <div className="mb-4 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">Limits &amp; owner report</div>
        <div className="grid items-end gap-4 sm:grid-cols-3">
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-400">Max auto-sends per day</span>
            <input type="number" min={0} max={1000} value={settings.max_auto_sends_per_day} data-testid="max-auto-sends"
              onChange={(e) => setData((d) => ({ ...d, settings: { ...d.settings, max_auto_sends_per_day: e.target.value } }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-[#1a5fd0]" />
          </label>
          <label className="block">
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-400">Owner report hour (Central)</span>
            <input type="number" min={0} max={23} value={settings.owner_report_hour} data-testid="owner-report-hour"
              onChange={(e) => setData((d) => ({ ...d, settings: { ...d.settings, owner_report_hour: e.target.value } }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-[#1a5fd0]" />
          </label>
          <label className="flex items-center gap-2 pb-2 text-[13px] text-slate-700">
            <input type="checkbox" checked={!!settings.email_report} data-testid="email-report-toggle"
              onChange={(e) => setData((d) => ({ ...d, settings: { ...d.settings, email_report: e.target.checked } }))} />
            Email the daily / weekly owner report
          </label>
        </div>
        <button onClick={saveLimits} disabled={saving} data-testid="save-limits-btn"
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#080d1a] px-5 py-2.5 font-head text-[14px] font-bold text-white hover:opacity-90 disabled:opacity-60">
          <Save size={15} /> {saving ? "Saving…" : "Save limits"}
        </button>
      </Card>
    </div>
  );
}
